import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NbDialogService, NbWindowService } from '@nebular/theme';
import TournamentBase from '../../tournament-base';
import { GetPlayerTrailResponse, PlayerData } from '../../tournament.interface';
import { MOCK_PLAYERS, MOCK_PLAYER_TRAIL } from '../../tournament.consts';
import { PlayerSelectionDialogComponent } from '../player-selection-dialog/player-selection-dialog.component';
import { TournamentService } from '../../tournament.service';
import { ActivatedRoute } from '@angular/router';
import { TeamResultsComponent } from '../../../../@theme/components/team-results/team-results.component';

@Component({
    selector: 'app-tournament-player-games',
    templateUrl: './player-games.component.html',
    styleUrls: ['./player-games.component.scss'],
})
export class TournamentPlayerGamesComponent extends TournamentBase implements OnInit {
    tournamentId: string = this.route.snapshot.paramMap.get('tournamentId');
    backRoute: string = '../';
    loading: boolean = false;
    playerTrailData: GetPlayerTrailResponse | null = null;
    selectedPlayer: string | null = null;
    selectedPlayerData: PlayerData | null = null;
    mockPlayers: PlayerData[] = MOCK_PLAYERS;
    selectedCategory: string = '';
    categories: { id_categoria: string; descricao: string }[] = [];
    groupGamesByDate: any = {};
    bracketGamesByDate: any = {};
    private subscription: Subscription = new Subscription();

    constructor(
        private dialogService: NbDialogService,
        private windowService: NbWindowService,
        protected tournamentService: TournamentService,
        protected route: ActivatedRoute
    ) {
        super();
    }

    ngOnInit(): void {
        this.init();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.subscription.unsubscribe();
    }

    init(): void {
        // Don't automatically select a player - wait for user selection
        this.selectedPlayerData = null;
        this.playerTrailData = null;
    }

    loadPlayerTrail(): void {
        if (!this.selectedPlayerData) {
            return;
        }

        this.loading = true;

        this.subscription.add(
            this.tournamentService
                .getPlayerTrail(this.tournamentId, this.selectedPlayerData.id_site, this.selectedCategory)
                .subscribe({
                    next: (response) => {
                        this.playerTrailData = response.registers;

                        // Process games by date for each category
                        this.groupGamesByDate = {};
                        this.bracketGamesByDate = {};

                        this.playerTrailData.games?.forEach((category, index) => {
                            if (category.group) {
                                this.groupGamesByDate[index] = this.getGamesByDate(category.group);
                            }
                            if (category.bracket) {
                                this.bracketGamesByDate[index] = this.getGamesByDate(category.bracket);
                            }
                        });

                        this.loading = false;
                    },
                    error: (error) => {
                        console.error('Error loading player trail:', error);
                        this.loading = false;
                    },
                })
        );
    }

    onPlayerChange(playerId: string): void {
        this.selectedPlayer = playerId;
        this.loadPlayerTrail();
    }

    doGroupGamesByDate(games: any[]): { [date: string]: any[] } {
        return games.reduce((groups, game) => {
            const date = game.dt_jogo;
            if (date) {
                if (!groups[date]) {
                    groups[date] = [];
                }
                groups[date].push(game);
            }
            return groups;
        }, {});
    }

    getGamesByDate(games: any[]): { date: string; games: any[] }[] {
        const grouped = this.doGroupGamesByDate(games);
        return Object.entries(grouped)
            .map(([date, games]) => ({
                date,
                games,
            }))
            .sort(
                (a, b) =>
                    new Date(a.date.split('/').reverse().join('-')).getTime() -
                    new Date(b.date.split('/').reverse().join('-')).getTime()
            );
    }

    hasGroupGames(category: any): boolean {
        return category.group && category.group.length > 0;
    }

    hasBracketGames(category: any): boolean {
        return category.bracket && category.bracket.length > 0;
    }

    openPlayerSelectionDialog(): void {
        this.dialogService
            .open(PlayerSelectionDialogComponent, {
                context: {
                    currentPlayer: this.selectedPlayerData,
                    tournamentId: this.tournamentId,
                },
            })
            .onClose.subscribe((selectedPlayer: PlayerData) => {
                if (selectedPlayer) {
                    this.selectPlayer(selectedPlayer);
                }
            });
    }

    selectPlayer(player: PlayerData): void {
        this.selectedPlayerData = player;
        this.selectedPlayer = player.nome_abreviado;

        this.subscription.add(
            this.tournamentService.getPlayerTrailFilter(this.tournamentId, player.id_site).subscribe({
                next: (filterResponse) => {
                    console.log('Available categories:', filterResponse.registers);
                    this.categories = filterResponse.registers;

                    this.loadPlayerTrail();
                },
                error: (error) => {
                    console.error('Error loading player trail filter:', error);
                },
            })
        );
    }

    onCategoryChange(category: string): void {
        this.selectedCategory = category;
        console.log('Selected category:', category);
        this.loadPlayerTrail();
    }

    openGameDetails(game: any): void {
        if (!game?.sub_games?.length) {
            return;
        }

        // Transform the game data to match the expected format for team-results component
        const participantes = {
            '1': {
                nome: this.getTeamName(game.players[1]),
                url_avatar: game.players[1][0]?.avatar || '',
            },
            '2': {
                nome: this.getTeamName(game.players[2]),
                url_avatar: game.players[2][0]?.avatar || '',
            },
        };

        // Transform sub_games to match the expected format
        const transformedSubGames = game.sub_games.map((subGame: any) => ({
            ...subGame,
            dt_jogo: subGame.dt_jogo || game.dt_jogo,
            hr_jogo: subGame.hr_jogo || game.hr_jogo,
            resultados: subGame.resultados || [],
        }));

        this.windowService.open(TeamResultsComponent, {
            title: 'Detalhes do confronto',
            context: {
                games: transformedSubGames,
                participantes: participantes,
            },
            windowClass: 'no-extra-buttons',
        });
    }

    private getTeamName(players: any[]): string {
        if (!players || players.length === 0) {
            return 'A definir';
        }

        if (players.length === 1) {
            return players[0].nome_abreviado;
        }

        return players.map((p) => p.nome_abreviado).join(' / ');
    }
}
